import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { getShellLoginUrl } from '@/config/shell';

interface MegaMenuColumn {
  icon: string;
  title: string;
  description: string;
  links: { label: string; href: string }[];
  accentBg: string;
  accentText: string;
  accentBorder: string;
  iconBg: string;
}

const megaMenuData: MegaMenuColumn[] = [
  {
    icon: 'ri-barcode-line',
    title: 'Processing',
    description: 'Clean, grade, weigh, vacuum-pack, and standardise harvest into trade-ready SKUs.',
    links: [
      { label: 'Processing Services', href: '/processing' },
      { label: 'Quality Standards', href: '/processing' },
      { label: 'SKU Standardisation', href: '/processing' },
    ],
    accentBg: 'bg-accent-50',
    accentText: 'text-accent-600',
    accentBorder: 'border-accent-200',
    iconBg: 'bg-accent-100 text-accent-600',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Storage',
    description: 'List your warehouse capacity, get matched demand.',
    links: [
      { label: 'Capacity Network', href: '/storage' },
      { label: 'For Warehouse Owners', href: '/storage' },
      { label: 'IoT & Monitoring', href: '/storage' },
    ],
    accentBg: 'bg-secondary-50',
    accentText: 'text-secondary-600',
    accentBorder: 'border-secondary-200',
    iconBg: 'bg-secondary-100 text-secondary-600',
  },
  {
    icon: 'ri-truck-line',
    title: 'Transport',
    description: "Lease your reefer van's spare space, move produce on demand.",
    links: [
      { label: 'Fleet Marketplace', href: '/transport' },
      { label: 'For Transporters', href: '/transport' },
      { label: 'Route & Load Matching', href: '/transport' },
    ],
    accentBg: 'bg-primary-50',
    accentText: 'text-primary-600',
    accentBorder: 'border-primary-200',
    iconBg: 'bg-primary-100 text-primary-600',
  },
  {
    icon: 'ri-exchange-line',
    title: 'Wholesale',
    description: 'Trade frozen seafood, meat & vegetables B2B, at scale.',
    links: [
      { label: 'For Buyers', href: '/wholesale' },
      { label: 'For Suppliers', href: '/wholesale' },
      { label: 'Trade Marketplace', href: '/wholesale' },
    ],
    accentBg: 'bg-accent-50',
    accentText: 'text-accent-600',
    accentBorder: 'border-accent-200',
    iconBg: 'bg-accent-100 text-accent-600',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Retail',
    description: 'Hygienic, standard-cut frozen food, delivered home.',
    links: [
      { label: 'Shop Categories', href: '/retail' },
      { label: 'Subscriptions', href: '/retail' },
      { label: 'Quality Promise', href: '/retail' },
    ],
    accentBg: 'bg-foreground-50',
    accentText: 'text-foreground-700',
    accentBorder: 'border-foreground-200',
    iconBg: 'bg-foreground-100 text-foreground-600',
  },
];

export default function Navbar() {
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setMegaMenuOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 200);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-card-50/95 backdrop-blur-md border-b border-card-200 shadow-sm'
          : 'bg-card-50 border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/51c2999678346de9bfca529f28d29556/a863f5d5d75fba3353a913ed2253dbc1.png"
              alt="FreshhLock"
              className="h-8 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Solutions Mega Menu Trigger */}
            <div
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                  megaMenuOpen
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-foreground-600 hover:text-primary-700 hover:bg-primary-50'
                }`}
              >
                Solutions
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
            </div>

            <Link
              to="/why-us"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              Why Us
            </Link>
            <Link
              to="/company"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              Company
            </Link>
            <Link
              to="/careers"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              Careers
            </Link>
            <Link
              to="/contact"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              Contact Us
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={getShellLoginUrl()}
              className="text-sm font-medium text-foreground-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Sign In
            </a>
            <button
              onClick={() => {
                if (window.location.pathname === '/contact') {
                  const el = document.getElementById('contact-form');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                } else {
                  navigate('/contact');
                  setTimeout(() => {
                    const el = document.getElementById('contact-form');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 300);
                }
              }}
              className="px-5 py-2.5 bg-accent-500 hover:bg-accent-600 text-paper-50 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              Partner With Us
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Full-Width Mega Menu Panel */}
      <div
        className={`hidden lg:block absolute left-0 right-0 top-[72px] z-40 transition-all duration-300 ease-out ${
          megaMenuOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Backdrop shadow */}
        <div className="absolute inset-x-0 top-0 h-[600px] -z-10 shadow-[0_8px_40px_rgba(0,0,0,0.12)]"></div>

        <div className="bg-ink-950 border-b border-ink-800/70 overflow-hidden">
          {/* Subtle top gradient accent */}
          <div className="h-px bg-gradient-to-r from-secondary-500/50 via-brand-500/50 to-accent-500/50"></div>

          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-[1280px] mx-auto py-8 md:py-10">
              {/* 5-column grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
                {megaMenuData.map((col) => (
                  <div
                    key={col.title}
                    className="group relative rounded-xl bg-ink-900/60 border border-ink-700/40 p-5 hover:bg-ink-800/60 transition-all duration-300"
                  >
                    {/* Accent top bar */}
                    <div className="absolute top-0 left-4 right-4 h-0.5 rounded-full bg-secondary-500 opacity-40 group-hover:opacity-100 transition-opacity"></div>

                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-900/40 text-secondary-400 shrink-0">
                        <i className={`${col.icon} text-lg`}></i>
                      </div>
                      <span className="font-heading font-semibold text-base text-secondary-400">
                        {col.title}
                      </span>
                    </div>

                    <p className="text-sm text-paper-400 mb-4 leading-relaxed">
                      {col.description}
                    </p>

                    <div className="space-y-2">
                      {col.links.map((link) => (
                        <Link
                          key={link.label}
                          to={link.href}
                          className="flex items-center gap-2 text-sm text-paper-300 hover:text-secondary-400 transition-colors py-1 group/link"
                        >
                          <i className="ri-arrow-right-s-line text-xs opacity-0 -ml-1 group-hover/link:opacity-100 group-hover/link:ml-0 transition-all text-secondary-400"></i>
                          <span>{link.label}</span>
                        </Link>
                      ))}
                    </div>

                    <Link
                      to={`/${col.title.toLowerCase()}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-secondary-400 hover:underline transition-all"
                    >
                      View {col.title}
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                ))}
              </div>

              {/* Bottom CTA bar */}
              <div className="mt-8 md:mt-10 pt-5 border-t border-ink-700/40">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-900/40 text-secondary-400">
                      <i className="ri-compass-3-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-secondary-400">
                        Not sure which pillar is right for you?
                      </p>
                      <p className="text-xs text-paper-400">
                        Take our 30-second quiz to find your match.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Link
                      to="/quiz"
                      className="px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-paper-50 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
                    >
                      Take the Quiz
                    </Link>
                    <button
                      onClick={() => {
                        if (window.location.pathname === '/') {
                          const el = document.getElementById('pillars');
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                          }
                        } else {
                          navigate({ pathname: '/', hash: 'pillars' });
                        }
                        setMegaMenuOpen(false);
                      }}
                      className="flex items-center gap-1.5 text-sm font-medium text-secondary-400 hover:text-secondary-300 transition-colors cursor-pointer"
                    >
                      See all five pillars
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[72px] z-40 bg-ink-900/95 backdrop-blur-md overflow-y-auto"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div className="px-5 pt-5 pb-8 space-y-2" onClick={(e) => e.stopPropagation()}>
            {/* Solutions Header */}
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-900/40 text-secondary-400">
                <i className="ri-apps-line text-base"></i>
              </div>
              <span className="font-heading text-xs font-bold text-secondary-400 uppercase tracking-wider">
                Our Solutions
              </span>
              <div className="flex-1 h-px bg-ink-600/50 ml-2"></div>
            </div>

            {/* Pillar Cards */}
            {megaMenuData.map((col) => (
              <div
                key={col.title}
                className="rounded-xl bg-ink-800/60 border border-ink-600/40 p-4 mb-2"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-900/40 text-secondary-400 shrink-0 mt-0.5">
                    <i className={`${col.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-heading font-bold text-sm text-secondary-400">
                      {col.title}
                    </span>
                    <p className="text-xs text-paper-400 mt-0.5 leading-relaxed">
                      {col.description}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      {col.links.map((link) => (
                        <Link
                          key={link.label}
                          to={link.href}
                          className="text-xs font-medium text-secondary-400/80 hover:text-secondary-400 transition-opacity py-0.5"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Gradient Divider */}
            <div className="py-3">
              <div className="h-px bg-gradient-to-r from-secondary-500/40 via-brand-500/40 to-accent-500/40"></div>
            </div>

            {/* Main Nav Links */}
            <div className="space-y-1">
              {[
                { to: '/why-us', icon: 'ri-shield-star-line', label: 'Why Us' },
                { to: '/company', icon: 'ri-building-4-line', label: 'Company' },
                { to: '/careers', icon: 'ri-briefcase-4-line', label: 'Careers' },
                { to: '/contact', icon: 'ri-customer-service-2-line', label: 'Contact Us' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-paper-300 hover:text-secondary-400 hover:bg-ink-800/60 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ink-700 text-paper-400">
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Bottom CTAs */}
            <div className="pt-4 space-y-3">
              <div className="h-px bg-ink-600/40"></div>
              <Link
                to="/quiz"
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-secondary-400 hover:bg-ink-800/60 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-900/40 text-secondary-400">
                  <i className="ri-compass-3-line text-base"></i>
                </div>
                Take the Quiz
              </Link>
              <div className="flex flex-col gap-3 px-1 pt-1">
                <a
                  href={getShellLoginUrl()}
                  className="w-full py-3 text-center text-sm font-semibold text-paper-300 border border-ink-600 rounded-xl hover:bg-ink-800 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </a>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (window.location.pathname === '/contact') {
                      const el = document.getElementById('contact-form');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                      }
                    } else {
                      navigate('/contact');
                      setTimeout(() => {
                        const el = document.getElementById('contact-form');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 300);
                    }
                  }}
                  className="w-full py-3 bg-secondary-500 hover:bg-secondary-600 text-paper-50 text-sm font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Partner With Us
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}